import { useState, useEffect, useRef } from 'preact/hooks';
import { Fragment } from 'preact';
import PropTypes from 'prop-types';
import AppState from '@state';

import loadOrgActiveWriters from '@actions/loadOrgActiveWriters';

import d from '@hooks/useIsoDateFormat';
import useWindowDimensions from '@hooks/useWindowDimensions';

import Card from '@distinct-components/cards/baseCard';
import BaseEmptyState from '@distinct-components/feedback/baseEmptyState';
import Text from '@ui-kit/typography/text';
import Box from '@ui-kit/box';
import Avatar from '@ui-kit/avatar';
import Spinner from '@ui-kit/loaders/Spinner';
import TableControls from '@ui-kit/table/tableControls';
import Table from '@ui-kit/table';
import TableHeader from '@ui-kit/table/tableHeader';
import TableRow from '@ui-kit/table/tableRow';
import TableCell from '@ui-kit/table/tableCell';
import TablePagination from '@ui-kit/table/tablePagination';

import { TableIllustrationWrapper, TableSearchEmptyPrompt } from './ActiveSongwritersStyles';

function ActiveSongwriters({ setSelectedWriter, addNew }) {
  const [isLoadingWriters, setIsLoadingWriters] = useState(true);
  const [searchActive, setSearchActive] = useState(false);
  const [writers, setWriters] = useState([]);
  const [currentPage, setCurrentPage] = useState(AppState.pagination.organizationSongwriter.value);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const { height } = useWindowDimensions();
  const pageRef = useRef(null);

  useEffect(() => {
    if (height < 1080) {
      setItemsPerPage(7);
    }
    setIsLoadingWriters(true);
    loadOrgActiveWriters(AppState.pubOrganization.id.value).then(() => {
      setTimeout(() => {
        setWriters(AppState.publisherActiveSongwriters.value.filter((w) => !!w.email));
        setSearchActive(false);
        setIsLoadingWriters(false);
      }, 800);
    });
  }, []);

  const indexOfLast = currentPage * itemsPerPage;
  const indexOfFirst = indexOfLast - itemsPerPage;
  const currentResults = writers.slice(indexOfFirst, indexOfLast);

  const paginate = (pageNumber) => {
    setCurrentPage(pageNumber);
    setTimeout(() => {
      pageRef.current.scrollIntoView({ behavior: 'instant', block: 'center' });
    }, 200);
  };

  const onSearchResults = (query) => {
    const results = [];
    setCurrentPage(1);
    if (query) {
      AppState.publisherActiveSongwriters.value.filter((w) => !!w.email).forEach((w) => {
        const name = `${w.firstName} ${w.lastName}`;
        if (name.toLocaleLowerCase().includes(query.toLocaleLowerCase())) {
          results.push(w);
        }
      });
      setWriters(results);
      setSearchActive(true);
    } else {
      setWriters(AppState.publisherActiveSongwriters.value.filter((w) => !!w.email));
      setSearchActive(false);
    }
  };

  return (
    isLoadingWriters ? (
      <Box width="100%" display="flex" justifyContent="center" height="calc(100vh - 200px)" alignItems="center">
        <Spinner size="2em" variant="page" />
      </Box>
    ) : (
      <Box>
        <div ref={pageRef} />
        {!searchActive && writers.length === 0
          ? (
            <Card variant="panel" mt="1.5em">
              <BaseEmptyState
                title="No Songwriters"
                message="To add a writer, craft a credential and send them a request"
                action={addNew}
                actionText="Add Songwriter"
                illustration="personas"
                minHeight="38em"
              />
            </Card>
          )
          : (
            <Fragment>
              <TableControls
                itemName="Songwriter"
                tableData={AppState.publisherActiveSongwriters.value.filter((w) => !!w.email)}
                currentResults={currentResults}
                indexOfFirst={indexOfFirst}
                indexOfLast={indexOfLast}
                showSearch
                searchActive={searchActive}
                searchResults={writers}
                handleSearch={onSearchResults}
              />
              <Table mb="1em">
                <TableHeader>
                  <TableCell widthPercent="48%" pr="0.75em">Name</TableCell>
                  <TableCell>IPI</TableCell>
                  <TableCell>Total Works</TableCell>
                  <TableCell>Credential Expires</TableCell>
                </TableHeader>
                {currentResults
                  .map((writer) => (
                    <TableRow onClick={() => setSelectedWriter(writer)} cursor>
                      <TableCell widthPercent="48%" pr="0.75em">
                        <TableIllustrationWrapper>
                          <Box mr="1em">
                            <Avatar
                              size="3em"
                              imgURL={writer.avatarURL}
                              isVerified={writer.isVerified}
                            />
                          </Box>
                          <Box pt="0.25rem">
                            <Text fontSize="0.938rem" color="var(--text-primary)" fontWeight="600" lineHeight="1.2">
                              {writer.firstName}
                              {' '}
                              {writer.lastName}
                            </Text>
                            <Text mt="0.125em" fontSize="0.688rem" maxWidth="16em" color="var(--text-secondary)" truncate>
                              id:
                              {writer.id}
                            </Text>
                          </Box>
                        </TableIllustrationWrapper>
                      </TableCell>
                      <TableCell mobileLabel="IPI:" color={!writer.ipi && 'var(--text-medium-mute)'}>{writer.ipi || 'Not Defined'}</TableCell>
                      <TableCell mobileLabel="Works:">{writer.totalCompositions}</TableCell>
                      <TableCell mobileLabel="Expires:">
                        {writer.expirationDate ? d(writer.expirationDate) : '(Until Revoked)'}
                      </TableCell>
                    </TableRow>
                  ))}
                {(searchActive && currentResults.length === 0)
              && (
                <TableSearchEmptyPrompt>
                  <Text color="var(--text-muted-panel)">No Writers Match Your Search</Text>
                </TableSearchEmptyPrompt>
              )}
              </Table>
              <TablePagination
                itemsPerPage={itemsPerPage}
                totalItems={writers.length}
                paginate={paginate}
                currentPage={currentPage}
              />
            </Fragment>
          )}

      </Box>
    )

  );
}

ActiveSongwriters.propTypes = {
  setSelectedWriter: PropTypes.func.isRequired,
  addNew: PropTypes.func.isRequired,
};

export default ActiveSongwriters;
